import { Link, useLocation, useSearchParams, useNavigate } from 'react-router-dom';
import { Search, Menu, X, ExternalLink, MapPin, Loader2 } from 'lucide-react';
import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

// Debounce hook
function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);

  return debouncedValue;
}

export function Header() {
  const { t } = useTranslation('common');
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const isSearchPage = location.pathname === '/search';
  const isProviderPage = location.pathname.startsWith('/provider');
  const isFullWidthPage = isSearchPage || isProviderPage;

  // Search state
  const [searchQuery, setSearchQuery] = useState(searchParams.get('loc') || '');
  const [isSearching, setIsSearching] = useState(false);
  const debouncedQuery = useDebounce(searchQuery, 400);

  // Update URL when debounced query changes (only on search page)
  useEffect(() => {
    if (isSearchPage) {
      const params = new URLSearchParams(searchParams);
      if (debouncedQuery) {
        params.set('loc', debouncedQuery);
      } else {
        params.delete('loc');
      }
      navigate(`/search?${params.toString()}`, { replace: true });
      setIsSearching(false);
    }
  }, [debouncedQuery, isSearchPage, navigate, searchParams]);

  // Handle input change
  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    if (isSearchPage) {
      setIsSearching(true);
    }
  }, [isSearchPage]);

  // Handle search submit (for non-search pages)
  const handleSearchSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    if (!isSearchPage && searchQuery) {
      navigate(`/search?loc=${encodeURIComponent(searchQuery)}`);
    }
  }, [isSearchPage, searchQuery, navigate]);

  // Clear search
  const handleClearSearch = useCallback(() => {
    setSearchQuery('');
    if (isSearchPage) {
      const params = new URLSearchParams(searchParams);
      params.delete('loc');
      navigate(`/search?${params.toString()}`, { replace: true });
    }
  }, [isSearchPage, navigate, searchParams]);

  // Sync search query with URL params when navigating to search page
  useEffect(() => {
    if (isSearchPage) {
      const locParam = searchParams.get('loc');
      if (locParam !== null && locParam !== searchQuery) {
        setSearchQuery(locParam);
      }
    }
  }, [isSearchPage, searchParams]);

  return (
    <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
      <div className={`mx-auto px-4 sm:px-6 lg:px-8 ${isFullWidthPage ? '' : 'max-w-7xl'}`}>
        <div className="flex items-center justify-between h-16 gap-4">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 flex-shrink-0">
            <div className="w-8 h-8 bg-primary-600 rounded-lg flex items-center justify-center">
              <Search className="w-5 h-5 text-white" />
            </div>
            <span className="text-xl font-bold text-gray-900 hidden sm:block">HVACMap</span>
          </Link>

          {/* Search Bar - Always visible, expands on search page */}
          <form
            onSubmit={handleSearchSubmit}
            className={`
              flex-1 max-w-2xl
              ${isSearchPage ? '' : 'hidden md:block'}
            `}
          >
            <div className="relative">
              <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={handleSearchChange}
                placeholder={t('searchPlaceholder', 'Search by city, state, or zip...')}
                className={`
                  w-full pl-10 pr-10 py-2.5 bg-gray-50 border border-gray-200 rounded-xl
                  text-gray-900 placeholder-gray-400
                  focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent focus:bg-white
                  transition-all
                `}
              />
              {/* Loading indicator or clear button */}
              <div className="absolute right-3 top-1/2 -translate-y-1/2">
                {isSearching ? (
                  <Loader2 className="w-5 h-5 text-primary-500 animate-spin" />
                ) : searchQuery ? (
                  <button
                    type="button"
                    onClick={handleClearSearch}
                    className="p-0.5 hover:bg-gray-200 rounded-full transition-colors"
                  >
                    <X className="w-4 h-4 text-gray-400" />
                  </button>
                ) : null}
              </div>
            </div>
          </form>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-4 flex-shrink-0">
            {!isSearchPage && (
              <Link
                to="/search"
                className="text-gray-600 hover:text-gray-900 font-medium"
              >
                {t('findPros')}
              </Link>
            )}
            <a
              href="https://hvacfrontdesk.com"
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-primary flex items-center gap-2"
            >
              {t('forPros')}
              <ExternalLink className="w-4 h-4" />
            </a>
          </nav>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2 flex-shrink-0"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? (
              <X className="w-6 h-6" />
            ) : (
              <Menu className="w-6 h-6" />
            )}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden border-t border-gray-200 bg-white">
          <div className="px-4 py-4 space-y-4">
            {/* Mobile Search */}
            {!isSearchPage && (
              <form onSubmit={handleSearchSubmit}>
                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={handleSearchChange}
                    placeholder={t('searchPlaceholder', 'Search by city, state, or zip...')}
                    className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
              </form>
            )}
            <Link
              to="/search"
              className="block text-gray-600 hover:text-gray-900 font-medium"
              onClick={() => setMobileMenuOpen(false)}
            >
              {t('findPros')}
            </Link>
            <a
              href="https://hvacfrontdesk.com"
              target="_blank"
              rel="noopener noreferrer"
              className="block btn btn-primary text-center"
              onClick={() => setMobileMenuOpen(false)}
            >
              {t('forPros')}
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
